import { useState } from "react";
import moment from "moment";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const formatMoney = (value) => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return "—";
  return `$${num.toFixed(2)}`;
};

const formatFieldChange = (before, after, key, label, formatter = (v) => v ?? "—") => {
  const beforeVal = before?.[key];
  const afterVal = after?.[key];
  if (String(beforeVal ?? "") === String(afterVal ?? "")) return null;
  return `${label}: ${formatter(beforeVal)} → ${formatter(afterVal)}`;
};

const actionBadge = (action) => {
  if (action === "delete") {
    return { label: "Deleted", className: "bg-red-100 text-red-700" };
  }
  if (action === "create") {
    return { label: "Added", className: "bg-emerald-100 text-emerald-700" };
  }
  return { label: "Updated", className: "bg-blue-100 text-blue-700" };
};

export function TipEditLogsPanel({
  title = "Record Change Activity Log",
  moduleLabel = "tip",
  logs = [],
  isLoading = false,
  canClear = false,
  onClear,
  isClearing = false,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleConfirmClear = async () => {
    if (!onClear) return;
    await onClear();
    setConfirmOpen(false);
  };

  const formattedModule = String(moduleLabel || "tip").toLowerCase();

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm mt-8">
      <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:px-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <p className="mt-1 text-sm text-gray-500">
            Audit view of all {formattedModule} tip adds, updates, and deletes — visible to Admin and Admin Leaderboard.
          </p>
        </div>

        {canClear && (
          <Button
            type="button"
            variant="outline"
            className="shrink-0 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
            disabled={isLoading || isClearing || logs.length === 0}
            onClick={() => setConfirmOpen(true)}
          >
            {isClearing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Clearing…
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Clear Log
              </>
            )}
          </Button>
        )}
      </div>

      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">Loading activity log…</div>
        ) : logs.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            No activity yet. Add, edit, or delete a {formattedModule} tip record to see it logged here.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">When</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Action</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Performed By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Record</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {logs.map((log) => {
                const before = log.before_data || {};
                const after = log.after_data || {};
                const badge = actionBadge(log.action);
                const changes = [
                  formatFieldChange(before, after, "employee", "Employee"),
                  formatFieldChange(before, after, "tip_out_cash", "Tip Out", formatMoney),
                  formatFieldChange(before, after, "tax", "Tax", formatMoney),
                  formatFieldChange(before, after, "owe_to_toast", "Owe To Toast", formatMoney),
                  formatFieldChange(before, after, "shift", "Shift"),
                  formatFieldChange(
                    before,
                    after,
                    "date",
                    "Date",
                    (v) => (v ? moment(v).format("MM/DD/YYYY hh:mm A") : "—")
                  ),
                ].filter(Boolean);

                return (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {log.created_at ? moment(log.created_at).format("MM/DD/YYYY hh:mm A") : "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${badge.className}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-800">
                      {log.edited_by || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      #{log.record_id}
                      {(before.employee || after.employee) && (
                        <span className="ml-1 text-gray-500">
                          ({after.employee || before.employee})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {log.action === "delete" ? (
                        <span className="text-red-600">
                          Removed tip out {formatMoney(before.tip_out_cash)} for {before.employee || "employee"}
                        </span>
                      ) : log.action === "create" ? (
                        <span className="text-emerald-700">
                          Added tip out {formatMoney(after.tip_out_cash)} for {after.employee || "employee"}
                          {after.shift ? ` · Shift: ${after.shift}` : ""}
                        </span>
                      ) : changes.length > 0 ? (
                        <ul className="list-disc space-y-0.5 pl-4">
                          {changes.map((change) => (
                            <li key={change}>{change}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-gray-400">Record updated</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear activity log?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes all {formattedModule} tip record change activity log entries.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClearing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={isClearing}
              onClick={(e) => {
                e.preventDefault();
                handleConfirmClear();
              }}
            >
              {isClearing ? "Clearing…" : "Clear Log"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default TipEditLogsPanel;
